"use client";

/* eslint-disable @next/next/no-html-link-for-pages -- This component is hydrated by both Next.js and the standalone Public Worker runtime. */

import { useEffect, useState } from "react";
import { copyTextToClipboard } from "@/lib/clipboard";
import { resolveProfileLink } from "@/lib/link-types";
import type { CompiledMarkdownDocument, ProfileContent, ProfileLink, PublicPresentation } from "@/lib/types";
import { LocaleSwitcher } from "./LocaleSwitcher";
import { ProfileReadingTracker } from "./ProfileReadingTracker";
import { ThemePreview } from "./ThemePreview";
import { useI18n } from "./I18nProvider";

type PublicProfileProps = {
  profile: ProfileContent | null;
  presentation?: PublicPresentation;
  status?: "not_found" | "unpublished" | "moderated" | "frozen";
  owner?: boolean;
  compiledMarkdown?: CompiledMarkdownDocument;
};

export function PublicProfile({ profile, presentation, status = "not_found", owner = false, compiledMarkdown }: PublicProfileProps) {
  const [toast, setToast] = useState("");
  const [ownerState, setOwnerState] = useState<boolean | null>(owner ? true : null);
  const { t } = useI18n();

  useEffect(() => {
    if (!profile || owner) return;
    void fetch("/api/metrics/view", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ profileId: profile.id, handle: profile.handle, referrer: document.referrer })
    })
      .then(async (response) => {
        if (!response.ok) return;
        const payload = await response.json() as { owner?: boolean };
        setOwnerState(payload.owner === true);
      })
      .catch(() => undefined);
  }, [owner, profile]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 1600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  async function openLink(link: ProfileLink) {
    const resolved = resolveProfileLink(link);
    if (resolved.action === "copy") {
      if (profile) {
        void fetch("/api/metrics/link-click", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ profileId: profile.id, handle: profile.handle, linkId: link.id })
        }).catch(() => undefined);
      }
      if (await copyTextToClipboard(resolved.copyText ?? "")) {
        setToast(t("public.copySuccess", { label: resolved.label }));
      } else {
        setToast(t("public.copyFailed"));
      }
      return;
    }
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- This outbound redirect also runs in the standalone Public Worker, without a Next.js router.
    if (profile && resolved.href) window.location.href = `/@${profile.handle}/out/${encodeURIComponent(link.id)}`;
  }

  if (!profile) {
    return (
      <main className="profile-route-shell">
        <div className="empty-state">
          <p className="eyebrow">{getStatusEyebrow(status, t)}</p>
          <h1>{getStatusTitle(status, t)}</h1>
          <a className="button button-primary" href="/create">
            {t("public.createMine")}
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="profile-route-shell profile-route-shell-published">
      <ProfileReadingTracker profile={profile} enabled={ownerState === false} />
      {ownerState === true && <OwnerBar />}
      <div className="public-profile-content-direction" dir="auto">
        <ThemePreview
          profile={presentation ? { ...profile, themeId: presentation.themeId } : profile}
          interactiveLinks
          showAttribution={presentation?.showAttribution ?? true}
          onLinkClick={openLink}
          linkRedirectBasePath={`/@${encodeURIComponent(profile.handle)}/out`}
          compiledMarkdown={compiledMarkdown}
        />
      </div>
      <footer className="public-report-footer">
        <a href={`/abuse?handle=${encodeURIComponent(profile.handle)}`}>{t("public.report")}</a>
        <LocaleSwitcher className="public-locale-switcher" />
      </footer>
      {toast && <div className="public-toast">{toast}</div>}
    </main>
  );
}

function OwnerBar() {
  const { t } = useI18n();
  return (
    <div className="owner-bar" aria-label={t("public.ownerAria")}>
      <a href="/dashboard">{t("public.owner.manage")}</a>
      <a href="/create">{t("public.owner.edit")}</a>
    </div>
  );
}

function getStatusEyebrow(status: NonNullable<PublicProfileProps["status"]>, translate: ReturnType<typeof useI18n>["t"]): string {
  if (status === "moderated" || status === "frozen") return translate("public.status.unavailable");
  return translate("public.status.notFound");
}

function getStatusTitle(status: NonNullable<PublicProfileProps["status"]>, translate: ReturnType<typeof useI18n>["t"]): string {
  if (status === "moderated") return translate("public.status.moderated");
  if (status === "frozen") return translate("public.status.frozen");
  return translate("public.status.unpublished");
}
